// @flow
import download from "downloadjs";
import Properties from "../../../Properties";
import { getInit, postInit, graphQLInit } from "../../global/types/constants";
import type { DispatchType } from "../../global/types/redux";
import {
  LicenceNode,
  CompanyNode,
  FacebaseNode,
  UserNode,
  CompanyAdminNode,
  LicenceAdminNode,
  UserAdminNode
} from "../types/GraphQlNodes";
import { apiFailCallbackHandle } from "../containers/ErrosDisplay";
import { buildAdminUserNodefromGraphQlNode } from "../components/companies/CompileCompanytoJson";
import {
  SET_COMPANY_INFO,
  UPDATE_COMPANY_LICENCE,
  UPDATE_COMPANY_CHILD,
  UPDATE_COMPANY_FACEBASE,
  UPDATE_COMPANY_USER,
  UPDATE_LICENCET_ITEM,
  UPDATE_USER_LICENCE,
  ADMIN_UPDATE_COMPANY_LICENCE,
  ADMIN_UPDATE_COMPANY_FACEBASE,
  ADMIN_UPDATE_COMPANY_LIST,
  ADMIN_UPDATE_COMPANY_CHILD,
  ADMIN_UPDATE_LICENCE_LIST,
  ADMIN_UPDATE_LICENCE_ITEM,
  ADMIN_LICENCE_UPDATE_ITEM,
  ADMIN_UPDATE_USER_LIST
} from "../../global/actions/actionNames";
import { updateAdminCompanyUser } from "../../admin/actions/companyAdminActions";

const root = Properties.api_root;

const setCompanyInfo = (company: *, infoOnly: boolean = false): * => ({
  type: SET_COMPANY_INFO,
  company,
  infoOnly
});

export const fetchMyCompanyGraphQl = (nextAction: Function): * => (
  dispatch: DispatchType
): * => {
  const graphQl = `
  {
    company { ${CompanyNode} }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      dispatch(setCompanyInfo(json.data.company));
      nextAction && nextAction(json.data.company);
    });
};

export const fetchChildCompanyGraphQl = (
  childId: string,
  nextAction?: Function,
  selectOption?: boolean
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  {
    node(id: "${childId}") {
      ... on Company {
        ${CompanyNode}
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (selectOption !== true) {
        dispatch(setCompanyInfo(json.data.node));
      }
      nextAction && nextAction(json.data.node);
    });
};

export const updateCompanyLicence = (licenceId: string): * => (
  dispatch: DispatchType, getState: *
): * => {
  if(getState().v3account.company.id){
    const graphQl = `
    {
      node(id: "${licenceId}") {
        ... on Licence {
          ${LicenceNode}
        }
      }
    }
    `;
    return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
      .then((response) => response.json())
      .then((json) => {
        dispatch({ type: UPDATE_COMPANY_LICENCE, data: json.data.node });
      });
  }
};

export const createCompanyLicence = (
  licenceType: string,
  noOfStreams: number,
  companyId: string,
  deviceCount: number,
  expiryDate: string,
  autoAssign: boolean,
  startDate: string,
  facebaseId: string,
  modules: Array<*>,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin?: boolean = false
): * => (dispatch: DispatchType): * => {
  const extraNode = isAdmin ? LicenceAdminNode : "";
  let moduleNode = "";
  if (!!modules) {
    
    // hack to add Evaluation type in licence, revert this logic when backend is migrated and db can be modified
    if(licenceType == "EVALUATION"){
      const nameList = modules.map((item: {name: string, version: string, data: string, selected: boolean}): string => item.name.toLowerCase());
  
      if (!(nameList.includes("monthly") || nameList.includes("yearly")) && !nameList.includes("evaluation")){
        modules.push({name: "evaluation", version: "1.0", data:"", selected: true});
      }
    }
    const moduleList = modules.map(
      (item: *): * =>
        `{name : "${item.name}", version : "${item.version}", data: "${item.data}"}`
    );
    moduleNode = `modules: [${moduleList}]`;
  }

  const graphQl = `
  mutation {
    createLicence(
      licenceType: ${licenceType},
      noOfStreams: ${noOfStreams},
      companyId: "${companyId}",
      deviceCount: ${deviceCount},
      expiryDate: "${expiryDate}",
      autoAssign: ${autoAssign},
      startDate: "${startDate}",
      facebaseId: "${facebaseId}",
      isAdmin: ${isAdmin},
      ${moduleNode}
    ){
      ok
      errors {
        field
        message
      }
      licence {
        ${LicenceNode}
        ${extraNode}
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createLicence;
        if (result.ok) {
          if (isAdmin) {
            dispatch({
              type: ADMIN_UPDATE_COMPANY_LICENCE,
              data: result.licence,
              companyId: companyId
            });
          } else {
            dispatch({ type: UPDATE_COMPANY_LICENCE, data: result.licence });
          }
          doneCallback && doneCallback(result.licence);
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch((err) => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const companyAssignDevice = (
  hostIds: Array<string>,
  licenceId: string,
  doneBack: Function,
  failBack: Function,
  companyId?: string | null = null,
  licenceAdmin?: boolean = false
): * => (dispatch: DispatchType): * => {
  const extraNode = companyId !== null || licenceAdmin ? LicenceAdminNode : "";
  const graphQl = `
  mutation {
    assignLicence(licenceId: "${licenceId}", hostIds: ${hostIds}){
      ok
      errors {
        ... on InvalidLicenceIdError {
          errorType
        }
        ... on DeviceAlreadyAssignedError {
          hostId
          errorType
        }
        ... on NoAvailableLicenceSlotsError {
          errorType
        }
      }
      licence {
        ${LicenceNode}
        ${extraNode}
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      const result = json.data.assignLicence;
      if (result.ok) {
        if (companyId !== null) {
          dispatch({
            type: ADMIN_UPDATE_COMPANY_LICENCE,
            data: result.licence,
            companyId: companyId
          });
        } else if (licenceAdmin) {
          dispatch({ type: ADMIN_UPDATE_LICENCE_ITEM, data: result.licence });
        } else {
          dispatch({ type: UPDATE_USER_LICENCE, data: result.licence });
          dispatch({ type: UPDATE_COMPANY_LICENCE, data: result.licence });
        }
        doneBack && doneBack();
      } else {
        failBack && failBack(apiFailCallbackHandle(result.errors, "licence"));
      }
    })
    .catch((err) => {
      failBack && failBack(apiFailCallbackHandle(err.message, "licence"));
    });
};

export const companyUnassignDevice = (
  hostIds: Array<string>,
  licenceId: string,
  doneBack: Function,
  failBack: Function,
  companyId?: string | null = null,
  licenceAdmin?: boolean = false
): * => (dispatch: DispatchType): * => {
  const extraNode = companyId !== null || licenceAdmin ? LicenceAdminNode : "";
  const graphQl = `
  mutation {
    unAssignLicence(licenceId: "${licenceId}", hostIds: ${hostIds}){
      ok
      errors {
        ... on InvalidLicenceIdError {
          errorType
        }
        ... on DeviceNotAssignedError {
          hostId
          errorType
        }
      }
      licence {
        ${LicenceNode}
        ${extraNode}
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      const result = json.data.unAssignLicence;
      if (result.ok) {
        if (companyId !== null) {
          dispatch({
            type: ADMIN_UPDATE_COMPANY_LICENCE,
            data: result.licence,
            companyId: companyId
          });
        } else if (licenceAdmin) {
          dispatch({ type: ADMIN_UPDATE_LICENCE_ITEM, data: result.licence });
        } else {
          dispatch({ type: UPDATE_USER_LICENCE, data: result.licence });
          dispatch({ type: UPDATE_COMPANY_LICENCE, data: result.licence });
        }
        doneBack && doneBack();
      } else {
        failBack && failBack(apiFailCallbackHandle(result.errors, "licence"));
      }
    })
    .catch((err) => {
      failBack && failBack(apiFailCallbackHandle(err.message, "licence"));
    });
};

export function companyDownloadLicence(
  host_id: string,
  licenceId: string,
  isOld: boolean,
  doneCall: Function,
  failBack: Function
): * {
  return (dispatch: DispatchType) => {
    fetch(
      root +
        "/api/v2/company/licences/" +
        licenceId +
        "?host_id=" +
        host_id +
        "&is_old=" +
        isOld,
      getInit
    )
      .then(function (response: Response): * {
        if (response.ok) {
          return response.text();
        } else {
          return response.json();
        }
      })
      .then(function (json): * {
        if (json.errors) {
          failBack && failBack(apiFailCallbackHandle(json.errors, "licence"));
          return Promise.reject();
        } else {
          return json;
        }
      })
      .then((textRes: string): * => {
        download(textRes, `${host_id}.imlic`, "text/csv");
        doneCall && doneCall();
      })
      .catch((err) => {
        failBack && failBack(apiFailCallbackHandle(err.message, "licence"));
      });
  };
}

export const createCompany = (
  contactNumber: string,
  companyName: string,
  notes: string,
  abn: string,
  parentId: string,
  contactEmail: string,
  postcode: string,
  contactName: string,
  city: string,
  country: string,
  state: string,
  suburb: string,
  address: string,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin?: boolean = false,
  authorisedReseller: boolean,
  companyType: string,
  groupName?: string = "",
  tierId?: string = "",
  specialType?: string = "",
  productSheetId?: string = "",
  noRedux?: boolean = false
): * => (dispatch: DispatchType): * => {
  const returnNode = isAdmin
    ? `adminCompany { ${CompanyAdminNode} }`
    : `company { ${CompanyNode} }`;
  const graphQl = `
  mutation {
    createCompany(
      contactNumber: "${contactNumber}",
      name: "${companyName}",
      notes: "${notes}",
      abn: "${abn}",
      childOf: "${parentId}",
      contactEmail: "${contactEmail}",
      postcode: "${postcode}",
      contactName: "${contactName}",
      authorisedReseller: ${authorisedReseller},
      city: "${city}",
      country: "${country}",
      state: "${state}",
      suburb: "${suburb}",
      address: "${address}",
      companyType: ${companyType},
      isAdmin: ${isAdmin},
      groupName: "${groupName}",
      tierId: "${tierId}",
      ${specialType.trim() !== "" ? "specialType:" + specialType + "," : ""}
      productSheetId: "${productSheetId}"
    ){
      ok
      errors: error {
        ...on ChildOfCompanyNotFound {
          message
          errorType
        }
        ...on CompanyAlreadyExists {
          message
          errorType
        }
        ...on CompanyTypeNotAllowed {
          message
      	  errorType
        }
        ...on TierNotFound {
          message
      	  errorType
        }
        ...on ProductSheetNotFound {
          message
      	  errorType
        }
      }
      ${returnNode}
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createCompany;
        if (result.ok) {
          if (!noRedux) {
            if (isAdmin) {
              dispatch({
                type: ADMIN_UPDATE_COMPANY_LIST,
                company: result.adminCompany
              });
              dispatch({
                type: ADMIN_UPDATE_COMPANY_CHILD,
                data: result.adminCompany,
                parentId: parentId
              });
            } else {
              dispatch(updateCompanyChild(result.company));
            }
          }
          doneCallback &&
            doneCallback(isAdmin ? result.adminCompany : result.company);
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch((err) => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updateCompany = (
  contactNumber: string,
  companyName: string,
  notes: string,
  abn: string,
  companyId: string,
  contactEmail: string,
  postcode: string,
  contactName: string,
  city: string,
  country: string,
  state: string,
  suburb: string,
  address: string,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin?: boolean = false,
  authorisedReseller: boolean,
  companyType: string,
  groupName?: string = "",
  tierId?: string = "",
  specialType?: string = "",
  productSheetId?: string = "",
  childOf?: string = ""
): * => (dispatch: DispatchType): * => {
  const returnNode = isAdmin
    ? `adminCompany { ${CompanyAdminNode} }`
    : `company { ${CompanyNode} }`;
  const adminNode = isAdmin
    ? `authorisedReseller: ${authorisedReseller}, companyType: ${companyType}, groupName: "${groupName}",
      tierId: "${tierId}", childOf: "${childOf}", ${
        specialType.trim() !== "" ? "specialType:" + specialType + "," : ""
      }
      productSheetId: "${!productSheetId ? "" : productSheetId}" `
    : "";
  const graphQl = `
  mutation {
    updateCompany(
      contactNumber: "${contactNumber}",
      name: "${companyName}",
      notes: "${notes}",
      abn: "${abn}",
      companyId: "${companyId}",
      contactEmail: "${contactEmail}",
      postcode: "${postcode}",
      contactName: "${contactName}",
      city: "${city}",
      country: "${country}",
      state: "${state}",
      suburb: "${suburb}",
      address: "${address}",
      isAdmin: ${isAdmin},
      ${adminNode}
    ){
      ok
      errors: error {
        ...on ChildOfCompanyNotFound {
          message
          errorType
        }
        ...on CompanyAlreadyExists {
          message
          errorType
        }
        ...on CompanyNotFound {
          message
          errorType
        }
        ...on CompanyTypeNotAllowed {
          message
      	  errorType
        }
        ...on TierNotFound {
          message
      	  errorType
        }
        ...on ProductSheetNotFound {
          message
      	  errorType
        }
      }
      ${returnNode}
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.updateCompany;
        if (result.ok) {
          if (isAdmin) {
            dispatch({
              type: ADMIN_UPDATE_COMPANY_LIST,
              company: { ...result.adminCompany }
            });
          } else {
            dispatch(setCompanyInfo(result.company, true));
          }
          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch((err) => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const updateCompanyChild = (company: Object): * => (
  dispatch: DispatchType
) => {
  dispatch({ type: UPDATE_COMPANY_CHILD, company: company });
};

export const updateCompanyUser = (userId: string, user: *): * => (
  dispatch: DispatchType
) => {
  dispatch({ type: UPDATE_COMPANY_USER, user: user, userId: userId });
};

export const createCompanyUser = (
  facebaseId: string,
  firstName: string,
  lastName: string,
  companyId: string,
  email: string,
  groups: Array<string>,
  doneCallback: Function,
  failCallBack: Function,
  isAdmin?: boolean = false,
  isUserAdmin?: boolean = false
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    createUser(
      facebaseId: "${facebaseId}",
      firstName: "${firstName}",
      lastName: "${lastName}",
      companyId: "${companyId}",
      email: "${email}",
      userGroups: ${JSON.stringify(groups)},
      isAdmin: ${isAdmin}
    ){
      ok
      errors {
        field
        message
      }
      user {
        ${UserNode}
        ${isUserAdmin ? UserAdminNode : ""}
      }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createUser;
        if (result.ok) {
          let jsonUser = { ...result.user };

          if (isUserAdmin) {
            let user = buildAdminUserNodefromGraphQlNode(
              jsonUser,
              email,
              "FORCE_CHANGE_PASSWORD"
            );
            dispatch({ type: ADMIN_UPDATE_USER_LIST, user: user });
          } else {
            jsonUser.email = email;
            jsonUser.user_status = false;
            if (isAdmin) {
              dispatch(updateAdminCompanyUser(companyId, jsonUser));
            } else {
              dispatch(updateCompanyUser("", jsonUser));
            }
          }

          doneCallback && doneCallback();
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch((err) => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export const createFacebase = (
  personLimit: number,
  online: boolean,
  name: string,
  companyId: string,
  doneCallback: Function,
  failCallBack: Function,
  model?: number,
  isAdmin?: boolean = false
): * => (dispatch: DispatchType): * => {
  const graphQl = `
  mutation {
    createFacebase(
      personLimit: ${personLimit},
      online: ${online},
      name: "${name}",
      ${model ? "model:" + model + "," : ""}
      companyId: "${companyId}",
      isAdmin: ${isAdmin}
    ){
      ok
      errors {
        field
        message
      }
      facebase { ${FacebaseNode} }
    }
  }
  `;
  return fetch(`${root}/api/v2/company/graphql`, graphQLInit(graphQl))
    .then((response) => response.json())
    .then((json) => {
      if (json.errors) {
        failCallBack && failCallBack(apiFailCallbackHandle(json.errors));
      } else {
        const result = json.data.createFacebase;
        if (result.ok) {
          if (isAdmin) {
            dispatch({
              type: ADMIN_UPDATE_COMPANY_FACEBASE,
              facebase: result.facebase,
              companyId: companyId
            });
          } else {
            dispatch({
              type: UPDATE_COMPANY_FACEBASE,
              facebase: result.facebase
            });
          }
          doneCallback && doneCallback(result.facebase);
        } else {
          failCallBack && failCallBack(apiFailCallbackHandle(result.errors));
        }
      }
    })
    .catch((err) => {
      failCallBack && failCallBack(apiFailCallbackHandle(err.message));
    });
};

export function getUserStatus(
  id: string,
  cognitoId: string,
  failBack: Function,
  doneBack: Function
): * {
  return (dispatch: DispatchType): * => {
    fetch(root + "/api/v2/company/users/" + cognitoId + "/status", getInit)
      .then(function (response: Response): * {
        if (response.ok) {
          return response.json();
        } else {
          failBack && failBack(response.statusText);
          return Promise.reject();
        }
      })
      .then(function (json) {
        if (json.errors) {
          failBack && failBack(JSON.stringify(json.errors));
        } else {
          let user = {
            email: json.status.email,
            user_status: json.status.user_status
          };
          dispatch(updateCompanyUser(id, user));
          doneBack && doneBack();
        }
      })
      .catch((err) => {
        failBack && failBack(err.message);
      });
  };
}

export function uploadLicencefile(
  companyId: string,
  licenceId: string,
  file: File,
  doneCall: Function,
  failBack: Function,
  reduxAction?: string = ""
): * {
  let postFormData = new FormData();
  postFormData.append("po_file", file);
  return (dispatch: DispatchType) => {
    fetch(
      root + "/api/v2/company/licences/" + licenceId + "/upload",
      postInit(postFormData)
    )
      .then((response) => response.json())
      .then(function (json) {
        if (json.errors) {
          failBack && failBack(apiFailCallbackHandle(json.errors, "licence"));
        } else {
          if (reduxAction === "adminCompany") {
            dispatch({
              type: ADMIN_UPDATE_LICENCE_LIST,
              companyId: companyId,
              licenceId: licenceId,
              fieldName: "poFile",
              value: json.po_file
            });
          } else if (reduxAction === "adminLicence") {
            dispatch({
              type: ADMIN_LICENCE_UPDATE_ITEM,
              licenceId: licenceId,
              fieldName: "poFile",
              value: json.po_file
            });
          } else if (reduxAction === "company") {
            dispatch({
              type: UPDATE_LICENCET_ITEM,
              licenceId: licenceId,
              fieldName: "poFile",
              value: json.po_file
            });
          }
          doneCall && doneCall();
        }
      })
      .catch((err) => {
        failBack && failBack(apiFailCallbackHandle(err.message, "licence"));
      });
  };
}

export function getLicenceFileUrl(
  licenceId: string,
  licenceExtentId: string | null,
  doneCall: Function,
  failBack: Function
): * {
  return (dispatch: DispatchType) => {
    fetch(
      root +
        "/api/v2/company/licences/" +
        licenceId +
        "/file" +
        (licenceExtentId ? "?licence_extent_id=" + licenceExtentId : ""),
      getInit
    )
      .then((response) => response.json())
      .then(function (json) {
        if (json.errors) {
          failBack && failBack(apiFailCallbackHandle(json.errors, "licence"));
        } else {
          doneCall && doneCall(json.url);
        }
      })
      .catch((err) => {
        failBack && failBack(apiFailCallbackHandle(err.message, "licence"));
      });
  };
}
